'use client'

import { useState, useMemo } from 'react'
import Link from 'next/link'
import type { BlogPost } from './blogData'
import {
  curriculumSections,
  curriculumGroupOrder,
  blogGroupMap,
  crossListedLessons,
  standaloneCategories,
  topics,
  type CurriculumKey,
} from './blogGroupData'
import {
  GroupTable,
  MobileGroup,
  CollapsibleSection,
  StandaloneCard,
  isNew,
  type TableRow,
} from './BlogTable'
import VideoPill from './VideoPill'

// ── Build curriculum rows ────────────────────────────────────────────

function buildCurriculumRows(
  posts: BlogPost[],
  sectionKey: CurriculumKey,
): Map<string, TableRow[]> {
  const groups = new Map<string, TableRow[]>()
  const orderedGroups = curriculumGroupOrder[sectionKey] || []
  orderedGroups.forEach((g) => groups.set(g, []))

  // Add articles
  posts.forEach((post) => {
    const meta = blogGroupMap[post.id]
    if (!meta) return
    if (!orderedGroups.includes(meta.group)) return
    const arr = groups.get(meta.group)
    if (arr) {
      arr.push({
        post,
        group: meta.group,
        displayTitle: meta.displayTitle,
        learningOutcome: meta.learningOutcome,
        order: meta.order,
      })
    }
  })

  // Add cross-listed lessons (canonical in another course, surfaced here too)
  Object.entries(crossListedLessons[sectionKey] ?? {}).forEach(([id, meta]) => {
    const post = posts.find((p) => p.id === id)
    if (!post || !orderedGroups.includes(meta.group)) return
    const arr = groups.get(meta.group)
    if (arr) {
      arr.push({
        post,
        group: meta.group,
        displayTitle: meta.displayTitle,
        learningOutcome: meta.learningOutcome,
        order: meta.order,
      })
    }
  })

  // Sort each group by order
  groups.forEach((rows) => {
    rows.sort((a, b) => a.order - b.order)
  })

  return groups
}

// ── Curriculum view for a single section ─────────────────────────────

function CurriculumView({
  posts,
  sectionKey,
}: {
  posts: BlogPost[]
  sectionKey: CurriculumKey
}) {
  const grouped = useMemo(
    () => buildCurriculumRows(posts, sectionKey),
    [posts, sectionKey],
  )

  let groupIdx = 0

  return (
    <>
      {/* Desktop */}
      <div className="hidden md:block">
        {Array.from(grouped.entries()).map(([groupName, rows]) => {
          if (rows.length === 0) return null
          groupIdx++
          return (
            <GroupTable
              key={groupName}
              groupName={groupName}
              groupIndex={groupIdx}
              rows={rows}
            />
          )
        })}
      </div>
      {/* Mobile */}
      <div className="md:hidden">
        {(() => {
          let mGroupIdx = 0
          return Array.from(grouped.entries()).map(([groupName, rows]) => {
            if (rows.length === 0) return null
            mGroupIdx++
            return (
              <MobileGroup
                key={groupName}
                groupName={groupName}
                groupIndex={mGroupIdx}
                rows={rows}
              />
            )
          })
        })()}
      </div>
    </>
  )
}

// ── All sections view (collapsible) ─────────────────────────────────

function AllSectionsView({ posts }: { posts: BlogPost[] }) {
  return (
    <div>
      {curriculumSections.map((section, sIdx) => {
        const grouped = buildCurriculumRows(posts, section.key)
        const count = Array.from(grouped.values()).reduce(
          (sum, rows) => sum + rows.length,
          0,
        )
        if (count === 0) return null

        return (
          <CollapsibleSection
            key={section.key}
            topicLabel={section.label}
            count={count}
            defaultOpen={sIdx === 0}
            icon={section.icon}
            blurb={section.blurb}
          >
            {(() => {
              let groupIdx = 0
              return (
                <>
                  <div className="hidden md:block">
                    {Array.from(grouped.entries()).map(([groupName, rows]) => {
                      if (rows.length === 0) return null
                      groupIdx++
                      return (
                        <GroupTable
                          key={groupName}
                          groupName={groupName}
                          groupIndex={groupIdx}
                          rows={rows}
                        />
                      )
                    })}
                  </div>
                  <div className="md:hidden">
                    {(() => {
                      let mIdx = 0
                      return Array.from(grouped.entries()).map(([groupName, rows]) => {
                        if (rows.length === 0) return null
                        mIdx++
                        return (
                          <MobileGroup
                            key={groupName}
                            groupName={groupName}
                            groupIndex={mIdx}
                            rows={rows}
                          />
                        )
                      })
                    })()}
                  </div>
                </>
              )
            })()}
          </CollapsibleSection>
        )
      })}
    </div>
  )
}

// ── Standalone guides section ────────────────────────────────────────

function StandaloneGuidesSection({ posts }: { posts: BlogPost[] }) {
  const postMap = useMemo(() => {
    const m = new Map<string, BlogPost>()
    posts.forEach((p) => m.set(p.id, p))
    return m
  }, [posts])

  const hasContent = standaloneCategories.some((cat) => cat.postIds.length > 0)
  if (!hasContent) return null

  return (
    <div className="mt-16 pt-12 border-t border-gray-800">
      <h2 className="text-lg font-semibold text-white mb-1">More Resources</h2>
      <p className="text-sm text-gray-500 mb-8">
        Comparisons, role guides, tool reviews, and builder stories.
      </p>

      {standaloneCategories.map((cat) => {
        const items: { title: string; href?: string; isVideo: boolean }[] = []

        cat.postIds.forEach((id) => {
          const post = postMap.get(id)
          if (post && !post.redirectTo) {
            items.push({
              title: post.title.replace(/\s*\(2026\)\s*/g, '').replace(/:.*/,  ''),
              href: `/blog/${post.id}`,
              isVideo: post.hasVideo ?? false,
            })
          }
        })

        if (items.length === 0) return null

        return (
          <div key={cat.label} className="mb-6">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
              {cat.label}
            </h3>
            <div className="flex flex-wrap gap-2">
              {items.map((item, i) => (
                <StandaloneCard
                  key={`${cat.label}-${i}`}
                  title={item.title}
                  href={item.href}
                  isVideo={item.isVideo}
                />
              ))}
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ── Search results ───────────────────────────────────────────────────

function SearchResults({ posts }: { posts: BlogPost[] }) {
  if (posts.length === 0) {
    return (
      <div className="text-center py-16">
        <p className="text-gray-500 text-lg">No articles found.</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col">
      {posts.map((post) => {
        const meta = blogGroupMap[post.id]
        return (
          <Link
            key={post.id}
            href={`/blog/${post.id}`}
            className="flex items-start gap-3 py-3 border-b border-gray-800 last:border-b-0 hover:bg-gray-800/20 transition-colors px-2 -mx-2 rounded"
          >
            <span className="text-sm mt-0.5 flex-shrink-0">📘</span>
            <div className="min-w-0">
              <p className="text-sm text-white font-medium">
                {post.hasVideo && <VideoPill className="mr-2 align-middle" />}
                {post.title}
              </p>
              {meta && (
                <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">
                  {meta.learningOutcome}
                </p>
              )}
              {isNew(post.publishedAt) && (
                <span className="inline-block mt-1 text-[10px] font-semibold text-emerald-400 bg-emerald-400/10 px-1.5 py-0.5 rounded">
                  NEW
                </span>
              )}
            </div>
          </Link>
        )
      })}
    </div>
  )
}

// ── Main component ───────────────────────────────────────────────────

export default function BlogPageClient({ posts }: { posts: BlogPost[] }) {
  const [activeTab, setActiveTab] = useState<string>('all')
  const [search, setSearch] = useState('')

  const visiblePosts = useMemo(() => {
    return posts.filter((p) => !p.redirectTo && !p.noindex)
  }, [posts])

  const filteredPosts = useMemo(() => {
    if (!search) return visiblePosts
    const q = search.toLowerCase()
    return visiblePosts.filter(
      (p) =>
        p.title.toLowerCase().includes(q) ||
        p.description.toLowerCase().includes(q) ||
        p.tags.some((t) => t.toLowerCase().includes(q)),
    )
  }, [visiblePosts, search])

  const countBySection = useMemo(() => {
    const counts: Record<string, number> = {}
    curriculumSections.forEach((section) => {
      const grouped = buildCurriculumRows(visiblePosts, section.key)
      counts[section.key] = Array.from(grouped.values()).reduce(
        (sum, rows) => sum + rows.length,
        0,
      )
    })
    counts['all'] = Object.values(counts).reduce((a, b) => a + b, 0)
    return counts
  }, [visiblePosts])

  const isSearching = search.length > 0

  return (
    <>
      {/* Tabs */}
      <div className="flex flex-col gap-4 mb-8">
        <div className="flex flex-wrap gap-2">
          {[{ key: 'all', label: 'All' }, ...curriculumSections].map((tab) => {
            const count = countBySection[tab.key] || 0
            const isActive = activeTab === tab.key
            return (
              <button
                key={tab.key}
                onClick={() => {
                  setActiveTab(tab.key)
                  setSearch('')
                }}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-white text-black'
                    : 'bg-gray-800/60 text-gray-400 hover:bg-gray-700 hover:text-white'
                }`}
              >
                {tab.label}
                <span
                  className={`ml-1.5 text-xs ${isActive ? 'text-gray-600' : 'text-gray-500'}`}
                >
                  {count}
                </span>
              </button>
            )
          })}
        </div>

        {/* Search */}
        <div className="relative sm:max-w-xs">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <input
            type="text"
            placeholder="Search lessons, guides & videos..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-gray-800/60 border border-gray-700 rounded-full pl-10 pr-4 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-gray-500 transition-colors"
          />
        </div>
      </div>

      {/* Content */}
      {isSearching ? (
        <SearchResults posts={filteredPosts} />
      ) : activeTab === 'all' ? (
        <>
          <AllSectionsView posts={visiblePosts} />
          <StandaloneGuidesSection posts={visiblePosts} />
        </>
      ) : (
        <>
          <CurriculumView
            posts={visiblePosts}
            sectionKey={activeTab as CurriculumKey}
          />
          {activeTab === 'claude-code' && (
            <StandaloneGuidesSection posts={visiblePosts} />
          )}
        </>
      )}
    </>
  )
}
